// components/UploadVirementProof.jsx
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Upload, X, FileUp, AlertTriangle, AlertCircle, Lock } from 'lucide-react';
import { toast } from 'react-toastify';
import imageCompression from 'browser-image-compression';
import config from '../../config';

const UploadVirementProof = ({ 
  orderId, 
  orderCode, 
  isSubscription = false,
  uploadAttempts = 0,
  maxAttempts = 3,
  onUploadSuccess 
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showMaxAttemptsModal, setShowMaxAttemptsModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [attemptsLeft, setAttemptsLeft] = useState(maxAttempts - uploadAttempts);
  const [compressing, setCompressing] = useState(false);
  
  // États pour la barre de progression
  const [uploadProgress, setUploadProgress] = useState({ type: "", value: 0 });
  const [submitStatus, setSubmitStatus] = useState('idle');

  // Mettre à jour les tentatives restantes
  useEffect(() => {
    setAttemptsLeft(maxAttempts - uploadAttempts);
  }, [uploadAttempts, maxAttempts]);

  // Vérifier si l'utilisateur a atteint le maximum de tentatives
  const hasReachedMaxAttempts = uploadAttempts >= maxAttempts;

  const openModal = () => {
    if (hasReachedMaxAttempts) {
      setShowMaxAttemptsModal(true);
      return;
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setShowConfirm(false);
    setFile(null);
    setPreviewUrl(null);
    setUploadProgress({ type: "", value: 0 });
    setSubmitStatus('idle');
  };

  const closeMaxAttemptsModal = () => {
    setShowMaxAttemptsModal(false);
  };

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  // Fonction de compression d'image avec progression
  const compressImage = async (imageFile) => {
    const options = {
      maxSizeMB: 1,
      maxWidthOrHeight: 1920,
      useWebWorker: true,
      fileType: 'image/jpeg',
      initialQuality: 0.8,
      onProgress: (progress) => {
        setUploadProgress({ type: "compression", value: Math.round(progress) });
      },
    };

    try {
      setCompressing(true);
      setUploadProgress({ type: "compression", value: 0 });
      
      if (!imageFile.type.startsWith('image/')) {
        return imageFile;
      }

      if (imageFile.size <= 1 * 1024 * 1024) {
        setUploadProgress({ type: "", value: 0 });
        return imageFile;
      }

      const compressedFile = await imageCompression(imageFile, options);
      
      const compressedBlob = new Blob([compressedFile], { type: compressedFile.type });
      const compressedImageFile = new File([compressedBlob], imageFile.name, {
        type: compressedFile.type,
        lastModified: Date.now(),
      });

      const originalSize = (imageFile.size / 1024 / 1024).toFixed(2);
      const compressedSize = (compressedImageFile.size / 1024 / 1024).toFixed(2);
      
      console.log(`Compression: ${originalSize}MB → ${compressedSize}MB`);
      
      if (compressedImageFile.size > 5 * 1024 * 1024) {
        toast.warning(`L'image compressée fait encore ${compressedSize}MB. Veuillez choisir une image plus petite.`);
        return null;
      }

      setUploadProgress({ type: "", value: 0 });
      return compressedImageFile;
    } catch (error) {
      console.error('Erreur lors de la compression:', error);
      toast.warning('Impossible de compresser l\'image. Envoi du fichier original.');
      return imageFile;
    } finally {
      setCompressing(false);
      setUploadProgress({ type: "", value: 0 });
    }
  };

  const handleFileChange = async (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;
    
    if (selectedFile.size > 10 * 1024 * 1024) {
      toast.error('Le fichier ne doit pas dépasser 10 Mo avant compression');
      return;
    }

    let finalFile = selectedFile;
    
    if (selectedFile.type.startsWith('image/')) {
      const compressedFile = await compressImage(selectedFile);
      if (!compressedFile) {
        return;
      }
      finalFile = compressedFile;
      
      if (finalFile.size > 5 * 1024 * 1024) {
        toast.error('Même après compression, le fichier dépasse 5 Mo. Veuillez choisir une image plus petite.');
        return;
      }
    } else {
      if (selectedFile.size > 5 * 1024 * 1024) {
        toast.error('Le fichier PDF ne doit pas dépasser 5 Mo');
        return;
      }
    }
    
    setFile(finalFile);
    
    if (finalFile.type.startsWith('image/')) {
      const url = URL.createObjectURL(finalFile);
      setPreviewUrl(url);
    } else {
      setPreviewUrl(null);
    }

    const originalSize = (selectedFile.size / 1024 / 1024).toFixed(2);
    const finalSize = (finalFile.size / 1024 / 1024).toFixed(2);
    
    if (selectedFile.type.startsWith('image/') && originalSize !== finalSize) {
      toast.success(`Image compressée : ${originalSize}MB → ${finalSize}MB`);
    }
  };

  const handleConfirmUpload = () => {
    if (!file) return toast.error('Aucun fichier sélectionné');
    
    if (attemptsLeft <= 0) {
      setShowMaxAttemptsModal(true);
      return;
    }
    
    setShowConfirm(true);
  };

  const proceedWithUpload = async () => {
    setUploading(true);
    setSubmitStatus('uploading');
    setShowConfirm(false);
    setUploadProgress({ type: "upload", value: 0 });
    
    const formData = new FormData();
    formData.append('proof', file);

    try {
      const token = localStorage.getItem('accessToken');
      if (!token) throw new Error("Token manquant");
      
      const endpoint = isSubscription
        ? `/upload-virement-proof-subscription/${orderId}`
        : `/upload-virement-proof/${orderId}`;
      
      const response = await axios.post(
        `${config.SITE_API_URL}${endpoint}`, 
        formData, 
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'x-api-key': config.SITE_API_KEY,
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              setUploadProgress({ type: "upload", value: percentCompleted });
            }
          },
        }
      );

      setSubmitStatus('complete');
      setUploadProgress({ type: "completed", value: 100 });
      
      toast.success(response.data.message || 'Preuve envoyée avec succès !');
      
      // Attendre un peu avant de fermer pour voir la barre à 100%
      setTimeout(() => {
        if (onUploadSuccess) {
          onUploadSuccess(response.data);
        } else {
          window.location.reload();
        }
        closeModal();
      }, 1000);
      
    } catch (error) {
      console.error('Erreur upload preuve :', error);
      setSubmitStatus('error');
      setUploadProgress({ type: "error", value: 0 });
      
      const msg = error.response?.data?.message || 'Erreur lors de l\'envoi';
      toast.error(msg);
      
      setTimeout(() => {
        setSubmitStatus('idle');
        setUploadProgress({ type: "", value: 0 });
      }, 2000);
    } finally {
      setUploading(false);
    }
  };

  // Palette de couleurs
  const palette = {
    primary: '#003479',
    primaryHover: '#002b63',
    secondary: '#0879bd',
    accent: '#f29f2f',
    accentHover: '#d88b1f',
    light: '#58bceb',
    warning: '#f59e0b',
    error: '#dc2626',
  };

  // Composant de barre de progression réutilisable
  const ProgressBar = ({ type, value }) => {
    if (!type || value === 0) return null;
    
    const getProgressColor = () => {
      if (type === 'error') return 'bg-red-500';
      if (type === 'completed') return 'bg-green-500';
      return 'bg-blue-500';
    };
    
    const getProgressText = () => {
      if (type === 'compression') return 'Compression de l\'image...';
      if (type === 'upload') return 'Envoi de la preuve...';
      if (type === 'completed') return 'Envoi terminé !';
      if (type === 'error') return 'Erreur lors de l\'envoi';
      return '';
    };
    
    return (
      <div className="w-full px-4 mb-4">
        <p className="text-center text-sm font-medium mb-2">
          {getProgressText()}
        </p>
        <div className="relative pt-1">
          <div className="flex mb-2 items-center justify-between">
            <div>
              <span className="text-xs font-semibold inline-block text-gray-600">
                {value}%
              </span>
            </div>
          </div>
          <div className="overflow-hidden h-2 mb-4 text-xs flex rounded bg-gray-200">
            <div
              style={{ width: `${value}%` }}
              className={`shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center ${getProgressColor()} transition-all duration-300`}
            ></div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <button
        onClick={openModal}
        className={`mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-lg transition-colors text-sm font-medium shadow-sm ${
          hasReachedMaxAttempts
            ? 'bg-gray-400 text-gray-700 cursor-not-allowed'
            : 'bg-[#003479] text-white hover:bg-[#002b63]'
        }`}
        disabled={hasReachedMaxAttempts}
        title={hasReachedMaxAttempts ? 'Maximum de tentatives atteint' : ''}
      >
        <Upload size={16} />
        {hasReachedMaxAttempts ? 'Tentatives épuisées' : 'Ajouter preuve de virement'}
        {!hasReachedMaxAttempts && attemptsLeft > 0 && (
          <span className="ml-2 text-xs bg-white/30 px-2 py-0.5 rounded">
            {attemptsLeft} tentatives restantes
          </span>
        )}
      </button>

      {/* Modal principal */}
      {isModalOpen && !showConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl overflow-hidden">
            <div 
              className="px-6 py-5 border-b flex items-center justify-between"
              style={{
                backgroundColor: `${palette.primary}0d`,
                borderColor: `${palette.primary}4d`,
              }}
            >
              <div className="flex items-center gap-3">
                <FileUp style={{ color: palette.primary }} size={26} />
                <div>
                  <h3 className="text-xlg font-semibold m-0" style={{ color: palette.primary }}>
                    Preuve de virement – {orderCode}
                  </h3>
                  {attemptsLeft > 0 && (
                    <p className="text-sm text-gray-600 mt-1">
                      <strong>Tentatives restantes :</strong> {attemptsLeft} / {maxAttempts}
                    </p>
                  )}
                </div>
              </div>
              <button onClick={closeModal} aria-label="Fermer">
                <X size={28} className="text-gray-600 hover:text-gray-900" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Barre de progression */}
              <ProgressBar type={uploadProgress.type} value={uploadProgress.value} />

              {attemptsLeft <= 1 && attemptsLeft > 0 && (
                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded">
                  <div className="flex items-center">
                    <AlertTriangle className="h-5 w-5 text-yellow-400 mr-3" />
                    <div>
                      <p className="text-sm text-yellow-800 font-medium">
                        Attention : Dernière tentative !
                      </p>
                      <p className="text-sm text-yellow-700">
                        C'est votre dernière tentative d'upload. Assurez-vous que votre preuve est claire et valide.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <p className="text-gray-700">
                Joignez une preuve claire (reçu bancaire, capture d'écran, etc.)
              </p>

              <label 
                className={`flex flex-col items-center justify-center border-2 border-dashed rounded-xl p-10 cursor-pointer transition-all hover:bg-gray-50 ${
                  compressing || uploadProgress.type === 'upload' ? 'opacity-50 cursor-wait' : ''
                }`}
                style={{
                  borderColor: palette.accent,
                }}
              >
                {compressing && uploadProgress.type === 'compression' ? (
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
                    <p className="text-gray-600">Compression de l'image en cours...</p>
                    <p className="text-sm text-gray-500 mt-2">{uploadProgress.value}%</p>
                  </div>
                ) : previewUrl ? (
                  <img
                    src={previewUrl}
                    alt="Prévisualisation"
                    className="max-h-64 object-contain rounded-lg mb-4 shadow-sm"
                  />
                ) : file ? (
                  <div className="text-center mb-4">
                    {file.type === 'application/pdf' ? (
                      <div className="text-center">
                        <FileUp className="h-16 w-16 text-gray-400 mx-auto mb-2" />
                        <p className="font-medium text-gray-800 break-all">{file.name}</p>
                        <p className="text-sm text-gray-600">
                          {(file.size / 1024 / 1024).toFixed(2)} Mo
                        </p>
                      </div>
                    ) : (
                      <>
                        <p className="font-medium text-gray-800 break-all">{file.name}</p>
                        <p className="text-sm text-gray-600">
                          {(file.size / 1024 / 1024).toFixed(2)} Mo
                        </p>
                      </>
                    )}
                  </div>
                ) : (
                  <>
                    <Upload style={{ color: palette.accent }} className="h-14 w-14 mb-4" />
                    <span className="text-lg font-medium" style={{ color: palette.accent }}>
                      Cliquez ou glissez votre fichier ici
                    </span>
                    <span className="text-sm text-gray-500 mt-2">
                      JPG, PNG, PDF • max 5 Mo (images compressées automatiquement)
                    </span>
                  </>
                )}
                <input
                  type="file"
                  accept="image/*,.pdf"
                  onChange={handleFileChange}
                  className="hidden"
                  disabled={compressing || uploadProgress.type === 'upload'}
                />
              </label>

              {file && !compressing && uploadProgress.type !== 'upload' && (
                <div className="flex justify-center">
                  <button
                    onClick={() => {
                      setFile(null);
                      setPreviewUrl(null);
                      setUploadProgress({ type: "", value: 0 });
                    }}
                    className="text-sm text-red-600 hover:text-red-800 underline"
                  >
                    Supprimer ce fichier
                  </button>
                </div>
              )}
            </div>

            <div className="bg-gray-50 px-6 py-5 flex justify-end gap-4 border-t">
              <button
                onClick={closeModal}
                className="px-6 py-2.5 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                disabled={uploadProgress.type === 'upload'}
              >
                Annuler
              </button>
              <button
                onClick={handleConfirmUpload}
                disabled={!file || uploading || compressing || uploadProgress.type === 'upload'}
                className={`px-6 py-2.5 rounded-lg font-medium min-w-40 flex items-center justify-center gap-2 transition-all shadow-sm ${
                  !file || uploading || compressing || uploadProgress.type === 'upload'
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : ''
                }`}
                style={{
                  backgroundColor: !file || uploading || compressing || uploadProgress.type === 'upload' ? undefined : palette.primary,
                  color: !file || uploading || compressing || uploadProgress.type === 'upload' ? undefined : 'white',
                }}
              >
                {uploadProgress.type === 'upload' ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Envoi... {uploadProgress.value}%
                  </>
                ) : compressing ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Compression...
                  </>
                ) : (
                  'Envoyer la preuve'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal confirmation */}
      {showConfirm && (
        <div className="fixed inset-0 z-60 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden">
            <div className="p-8 text-center">
              <AlertTriangle
                style={{ color: palette.accent }} 
                className="mx-auto mb-5" 
                size={56} 
              />
              <h4 className="text-2xl font-bold text-gray-900 mb-3">
                Confirmer l'envoi ?
              </h4>
              <p className="text-gray-600 mb-8">
                Vous allez transmettre la preuve pour{' '}
                <strong style={{ color: palette.primary }}>{orderCode}</strong>.
                <br />
                Cette action est irréversible.
                {attemptsLeft === 1 && (
                  <div className="mt-4 p-3 bg-yellow-50 rounded-lg">
                    <p className="text-yellow-700 font-medium">
                      ⚠️ C'est votre dernière tentative !
                    </p>
                  </div>
                )}
              </p>
              <div className="flex justify-center gap-5">
                <button
                  onClick={() => setShowConfirm(false)}
                  className="px-8 py-3 bg-gray-200 text-gray-800 rounded-xl hover:bg-gray-300 transition-colors font-medium"
                >
                  Annuler
                </button>
                <button
                  onClick={proceedWithUpload}
                  disabled={uploading}
                  className={`px-8 py-3 rounded-xl font-medium min-w-[160px] flex items-center justify-center gap-2 transition-all shadow-md ${
                    uploading
                      ? 'bg-gray-400 text-white cursor-not-allowed'
                      : ''
                  }`}
                  style={{
                    backgroundColor: uploading ? undefined : palette.primary,
                    color: uploading ? undefined : 'white',
                  }}
                >
                  {uploading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Envoi...
                    </>
                  ) : (
                    'Confirmer & Envoyer'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal maximum de tentatives atteint */}
      {showMaxAttemptsModal && (
        <div className="fixed inset-0 z-70 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="p-8 text-center">
              <div className="mx-auto mb-6 w-20 h-20 bg-red-100 rounded-full flex items-center justify-center">
                <Lock className="w-10 h-10 text-red-600" />
              </div>
              
              <h4 className="text-2xl font-bold text-gray-900 mb-4">
                Maximum de tentatives atteint
              </h4>
              
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <div className="flex items-start">
                  <AlertCircle className="h-6 w-6 text-red-500 mr-3 flex-shrink-0 mt-0.5" />
                  <div className="text-left">
                    <p className="text-red-800 font-medium mb-2">
                      Vous avez atteint le nombre maximum de tentatives ({maxAttempts})
                    </p>
                    <p className="text-red-700 text-sm">
                      Vous ne pouvez plus télécharger de preuve pour cette transaction.
                      Veuillez contacter le support pour obtenir de l'aide.
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <p className="text-gray-600">
                  Référence : <strong style={{ color: palette.primary }}>{orderCode}</strong>
                </p>
                
                <div className="flex flex-col sm:flex-row justify-center gap-4">
                  <button
                    onClick={closeMaxAttemptsModal}
                    className="px-6 py-3 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                  >
                    Fermer
                  </button>
                  <a
                    href="mailto:support@jaafar-academy.com"
                    className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium inline-flex items-center justify-center gap-2"
                  >
                    Contacter le support
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default UploadVirementProof;